import {
  IMPORT_RESOLUTION_CREATE,
  IMPORT_RESOLUTION_KEEP,
  IMPORT_RESOLUTION_SKIP,
  ImportAmbiguousRowDto,
  ImportDecisionKind,
} from "../../../../domain/DataKitDto";

/**
 * The importer's "Needs a decision" step as plain data: which rows form one card, what each card's
 * choice sends to the server, and whether the card still needs an answer.
 *
 * The rule the whole step hangs on (Oct 9, QA): a row on a card is never dropped without the
 * member choosing it. Same-name rows default to "Keep as separate items"; every other card needs an
 * explicit answer, and Import stays disabled until it has one.
 */

/** A same-name card's choice - the three the review design offers. */
export type CardMode = "separate" | "one" | "skip";

export interface DecisionState {
  /** Per card: FILE_DUPLICATE (default "separate") and UPDATE_COLLISION (no default). */
  modes: Record<string, CardMode>;
  /** Per card in "one" mode: the row number kept. */
  keep: Record<string, number>;
  /** Per row that needs its own pick: a candidate item id, or the create/skip resolution. */
  picks: Record<number, string>;
}

export const emptyDecisionState = (): DecisionState => ({ modes: {}, keep: {}, picks: {} });

export interface DecisionCard {
  key: string;
  kind: ImportDecisionKind;
  rows: ImportAmbiguousRowDto[];
}

export interface CardResult {
  /** What this card sends as `resolutions`, row number -> value. */
  resolutions: Record<number, string>;
  /** False while the card still needs an answer - Import is disabled until every card has one. */
  resolved: boolean;
  /** Rows this card will write (new or changed items), for the Import count. */
  importing: number;
  /** Rows going ahead, for the Groups tab. */
  keptRows: number[];
}

/** Matches (pick an item) first, then same-name cards, then the rest - the review design's order. */
export const KIND_ORDER: ImportDecisionKind[] = [
  "KIT_MATCH",
  "FILE_DUPLICATE",
  "UPDATE_COLLISION",
  "ARCHIVED_ID",
  "EXTERNAL_ID_CONFLICT",
];

export function cardsOf(ambiguous: ImportAmbiguousRowDto[]): DecisionCard[] {
  const byKey = new Map<string, DecisionCard>();
  ambiguous.forEach((a) => {
    const key = a.setKey || `row\u001f${a.row.rowNumber}`;
    const card = byKey.get(key) || { key, kind: a.kind || "KIT_MATCH", rows: [] };
    card.rows.push(a);
    byKey.set(key, card);
  });
  return Array.from(byKey.values()).sort(
    (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)
  );
}

export const modeOf = (card: DecisionCard, state: DecisionState): CardMode | undefined =>
  state.modes[card.key] || (card.kind === "FILE_DUPLICATE" ? "separate" : undefined);

/** One kept same-name row: what it sends, whether it counts, and whether it is answered. */
function keptRow(a: ImportAmbiguousRowDto, state: DecisionState) {
  if (a.outcome === "CHOOSE") {
    const pick = state.picks[a.row.rowNumber];
    return { value: pick, resolved: !!pick, writes: !!pick && pick !== IMPORT_RESOLUTION_SKIP };
  }
  return { value: IMPORT_RESOLUTION_KEEP, resolved: true, writes: a.outcome !== "UP_TO_DATE" };
}

export function resolveCard(card: DecisionCard, state: DecisionState): CardResult {
  const resolutions: Record<number, string> = {};
  const keptRows: number[] = [];
  let importing = 0;
  let resolved = true;
  const skipAll = () => card.rows.forEach((a) => (resolutions[a.row.rowNumber] = IMPORT_RESOLUTION_SKIP));

  switch (card.kind) {
    case "EXTERNAL_ID_CONFLICT":
      // Never importable as the file stands - listed, not decided.
      break;

    case "FILE_DUPLICATE": {
      const mode = modeOf(card, state);
      if (mode === "skip") {
        skipAll();
        break;
      }
      const kept = mode === "one" ? state.keep[card.key] : undefined;
      if (mode === "one" && kept === undefined) resolved = false;
      card.rows.forEach((a) => {
        const rowNumber = a.row.rowNumber;
        if (mode === "one" && rowNumber !== kept) {
          resolutions[rowNumber] = IMPORT_RESOLUTION_SKIP;
          return;
        }
        if (mode === "one" && kept === undefined) return;
        const k = keptRow(a, state);
        if (!k.resolved) resolved = false;
        if (k.value) resolutions[rowNumber] = k.value;
        if (k.writes) importing++;
        if (k.value && k.value !== IMPORT_RESOLUTION_SKIP) keptRows.push(rowNumber);
      });
      break;
    }

    case "UPDATE_COLLISION": {
      const mode = modeOf(card, state);
      if (mode === "skip") {
        skipAll();
        break;
      }
      const kept = state.keep[card.key];
      if (mode !== "one" || kept === undefined) {
        resolved = false;
        break;
      }
      card.rows.forEach((a) => {
        if (a.row.rowNumber === kept && a.candidateItemIds[0]) {
          resolutions[a.row.rowNumber] = a.candidateItemIds[0];
          keptRows.push(a.row.rowNumber);
          importing++;
        } else {
          resolutions[a.row.rowNumber] = IMPORT_RESOLUTION_SKIP;
        }
      });
      break;
    }

    default:
      // KIT_MATCH, ARCHIVED_ID: every row picks its own answer.
      card.rows.forEach((a) => {
        const pick = state.picks[a.row.rowNumber];
        if (!pick) {
          resolved = false;
          return;
        }
        resolutions[a.row.rowNumber] = pick;
        if (pick !== IMPORT_RESOLUTION_SKIP) {
          importing++;
          keptRows.push(a.row.rowNumber);
        }
      });
  }
  return { resolutions, resolved, importing, keptRows };
}

/** "Add all": every same-name card back to keeping its rows as separate items. */
export function addAll(cards: DecisionCard[], state: DecisionState): DecisionState {
  const modes = { ...state.modes };
  cards.filter((c) => c.kind === "FILE_DUPLICATE").forEach((c) => (modes[c.key] = "separate"));
  return { ...state, modes };
}

/** "Skip all": every card that can be answered, answered with skip. */
export function skipAll(cards: DecisionCard[], state: DecisionState): DecisionState {
  const modes = { ...state.modes };
  const picks = { ...state.picks };
  cards.forEach((c) => {
    if (c.kind === "FILE_DUPLICATE" || c.kind === "UPDATE_COLLISION") modes[c.key] = "skip";
    else if (c.kind !== "EXTERNAL_ID_CONFLICT")
      c.rows.forEach((a) => (picks[a.row.rowNumber] = IMPORT_RESOLUTION_SKIP));
  });
  return { ...state, modes, picks };
}

export { IMPORT_RESOLUTION_CREATE, IMPORT_RESOLUTION_SKIP };
